import React, { useCallback, useEffect, useState } from "react";
import {
  clone,
  filter,
  get,
  head,
  includes,
  isEmpty,
  map,
  size,
  union,
  without,
  slice,
} from "lodash";
import { showError, showSuccess } from "../v2_actions/error";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import DeleteIcon from "@material-ui/icons/Delete";
import Gallery from "react-photo-gallery";
import { GalleryPhotograph } from "./GalleryPhotograph";
import LoadingSpinner from "components/LoadingSpinner";
import { PageLayout } from "./layout/PageLayout";
import { PhotographEditor } from "./PhotographEditor";
import RadioButtonCheckedIcon from "@material-ui/icons/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import { confirmModal } from "../v2_actions/ui";
import { setSelectedGallery } from "../actions/gallery";
import { galleryList, galleryDetailedList } from "../v2_actions/gallery";
import { getUserStoredCountry } from "../v2_actions/auth_country";
import { makeStyles, Button, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, IconButton } from "@material-ui/core";
import { photographList, deleteSelectedPhotographs } from "../v2_actions/photograph";
import SearchIcon from "@material-ui/icons/Search";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CloseIcon from "@material-ui/icons/Close";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";

const ROW_HEIGHT = "200px";

const useStyles = makeStyles({
  eyeIconButton: {
    position: "absolute",
    top: "10px",
    right: "10px",
    backgroundColor: "white",
    border: "1px solid #ccc",
    borderRadius: "50%",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 10,
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  deleteIconButton: {
    position: "absolute",
    top: "10px",
    right: "50px",
    backgroundColor: "white",
    border: "1px solid #ccc",
    borderRadius: "50%",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 10,
    "&:hover": {
      backgroundColor: "#ffebee",
    },
  },
  soldIconButton: {
    position: "absolute",
    top: "10px",
    right: "90px",
    border: "1px solid #ccc",
    borderRadius: "50%",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 10,
    "&:hover": {
      backgroundColor: "#ffebee",
    },
  },
  soldIconButtonActive: {
    backgroundColor: "#2196f3",
    color: "white",
  },
  soldIconButtonInactive: {
    backgroundColor: "white",
    color: "inherit",
  },
  searchBox: {
    position: "absolute",
    top: "10px",
    left: "10px",
    backgroundColor: "white",
    width: "250px",
    zIndex: 10,
  },
  photographs_container: {
    position: "relative",
    display: "flex",
    flexWrap: "wrap",
    overflowY: "scroll",
    border: "solid 1px",
    width: "100%",
    paddingTop: "60px",
  },
  photograph_container: {
    margin: "10px",
    position: "relative",
    height: ROW_HEIGHT,
    backgroundColor: "lightgray",
  },
  photograph: {
    height: ROW_HEIGHT,
  },
  gallerySpecificAction: {
    display: "flex",
    alignItems: "center",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    gap: "10px",
  },
  loadMoreButton: {
    backgroundColor: "#0844a4",
    color: "white",
    padding: "12px 32px",
    fontSize: "16px",
    fontWeight: 600,
    borderRadius: 8,
    textTransform: "capitalize",
    "&:hover": {
      backgroundColor: "#063589",
    },
  },
  dialogTitle: {
    position: "relative",
    paddingRight: "50px",
    fontWeight: "bold",
    color: "#000",
  },
  closeButton: {
    position: "absolute",
    right: "8px",
    top: "8px",
  },
  photoDetail: {
    marginBottom: "15px",
    padding: "10px",
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
  },
  photoDetailLabel: {
    fontWeight: "bold",
    marginRight: "10px",
  },
});

export const GalleryView = ({ match }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { gallery_id } = useParams();
  const searchParams = window.location.search;
  const urlParams = new URLSearchParams(searchParams);
  const isUpdated = urlParams.get("updated");
  const country = getUserStoredCountry();
  const gallery = useSelector(() => galleryList.getObject(gallery_id));
  const loadingGalleries = useSelector(() => galleryList.isLoading());
  const galleries = useSelector(() => galleryList.getVisibleObjects());
  const isLoading = galleryList.isLoading();
  const [firstGalleryId, setFirstGalleryId] = useState(null);
  const [defaultGalleryUrl, setDefaultGalleryUrl] = useState(false);
  const [activePhotographId, setActivePhotographId] = useState(null);
  const [selectedPhotographIds, setSelectedPhotographIds] = useState([]);
  const firstGallery = useSelector(() => galleryList.getObject(firstGalleryId));
  const galleryPhotographs = useSelector(() =>
    photographList.getVisibleObjects(),
  );
  const [editingPhotographId, setEditingPhotographId] = useState(null);
  const [photosToShow, setPhotosToShow] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const [showViewPopup, setShowViewPopup] = useState(false);
  const [showSoldIcons, setShowSoldIcons] = useState(false);

  const PHOTOS_PER_PAGE = 20;

  useEffect(() => {
    if (isUpdated) dispatch(galleryList.invalidateList());
    dispatch(galleryList.fetchListIfNeeded());
  }, [isUpdated]);

  useEffect(() => {
    /* when this page is reached via dashboardRoutes
     * path is set to /photographer/gallery/:id/view
     * fetching with :id causes an exception on the api */
    if (gallery_id && gallery_id !== ":id") {
      dispatch(photographList.fetchGalleryPhotographs(gallery_id));
    }
  }, [isUpdated, gallery_id]);

  useEffect(() => {
    if (gallery_id) {
      dispatch(galleryDetailedList.get({ galleryRef: gallery_id })).then((result) => {
        if (result) {
          dispatch(setSelectedGallery(result));
        }
      });
    }
  }, [gallery_id]);

  useEffect(() => {
    /* when this page is reached via dashboardRoutes
     * path is set to /photographer/gallery/:id/view
     * get first visible gallery and reload page */
    if (includes(gallery_id, ":id")) {
      dispatch(galleryList.fetchListIfNeeded());
      setFirstGalleryId(get(head(galleries), "id"));
      dispatch(galleryList.ensureObjectLoaded(gallery_id));
    }
  }, [gallery_id, galleries, firstGallery]);

  useEffect(() => {
    if (!defaultGalleryUrl && !isEmpty(firstGallery)) {
      history.push(`/photographer/gallery/${get(firstGallery, "id")}/view`);
      setDefaultGalleryUrl(true);
    }
  }, [firstGallery]);

  const onDeletePhotograph = (photograph) => {
    const onDeleted = (json) => {
      if (json.errors) {
        showError("Error", JSON.stringify(json.errors));
      } else {
        dispatch(photographList.fetchGalleryPhotographs(gallery_id));
        dispatch(galleryList.invalidateList());
        dispatch(galleryList.fetchListIfNeeded());
        showSuccess("Removed", "Removed from the gallery");
      }
    };

    const onConfirmed = () => {
      const values = Object.assign({}, photograph, {
        available_to_purchase: false,
      });
      dispatch(photographList.saveObject(values)).then(onDeleted);
    };

    dispatch(
      confirmModal({
        text: "Are you sure you want to make this photograph unavailable to purchase?",
        onConfirmed: onConfirmed,
        can_cancel: true,
      }),
    );
  };

  const onDeletePhotographWithoutGallerySizeFileInfo = (photograph) => {
    const onDeleted = (json) => {
      if (json.errors) {
        showError("Error", JSON.stringify(json.errors));
      } else {
        dispatch(photographList.fetchGalleryPhotographs(gallery_id));
        dispatch(galleryList.invalidateList());
        dispatch(galleryList.fetchListIfNeeded());
        showSuccess("Removed", `Photo: ${photograph.photo_name}`);
      }
    };
    //dispatch(photographList.deleteObject(photograph.id)).then(onDeleted);
  };

  const onSetAsMapPhotograph = (photograph) => {
    const onSaved = (json) => {
      if (json.errors) {
        showError("Error", JSON.stringify(json.errors));
      } else {
        dispatch(galleryList.invalidateList());
        dispatch(galleryList.fetchListIfNeeded());
        showSuccess("Set", "Set as map photograph");
      }
    };

    const onConfirmed = () => {
      const values = {
        id: gallery.id,
        map_photograph: photograph.id,
      };
      dispatch(galleryList.patchObject(values)).then(onSaved);
    };

    dispatch(
      confirmModal({
        text: "Are you sure you want to make this the map photograph?",
        onConfirmed: onConfirmed,
        can_cancel: true,
      }),
    );
  };

  const onStartEditPhotograph = (photograph) => {
    setEditingPhotographId(photograph.id);
  };

  const onStopEditPhotograph = () => {
    setEditingPhotographId(null);
  };

  const handleToggleSoldIcons = () => {
    setShowSoldIcons(!showSoldIcons);
  };

  const onActivatePhotograph = (photograph) => {
    if (photograph) {
      setActivePhotographId(photograph.id);
    } else {
      setActivePhotographId(null);
    }
  };

  const onSelectAllPhotographs = () => {
    setSelectedPhotographIds(
      map(gallery.photographs, (photograph) => photograph.id),
    );
  };

  const onUnSelectAllPhotographs = () => {
    setSelectedPhotographIds([]);
  };

  const onSelectPhotograph = (photograph) => {
    setSelectedPhotographIds(union(selectedPhotographIds, [photograph.id]));
  };

  const onUnSelectPhotograph = (photograph) => {
    setSelectedPhotographIds(without(selectedPhotographIds, photograph.id));
  };

  const getIsSelected = (photograph) => {
    return includes(selectedPhotographIds, photograph.id);
  };

  const getIsActivated = (photograph) => {
    return activePhotographId === photograph.id || getIsSelected(photograph);
  };

  const imageRenderer = useCallback(
    function ({ index, left, top, key, photo }) {
      return (
        <GalleryPhotograph
          getIsSelected={getIsSelected}
          getIsActivated={getIsActivated}
          onActivatePhotograph={onActivatePhotograph}
          onSelectPhotograph={onSelectPhotograph}
          onUnSelectPhotograph={onUnSelectPhotograph}
          onDeletePhotograph={onDeletePhotograph}
          onDeletePhotographWithoutGallerySizeFileInfo={
            onDeletePhotographWithoutGallerySizeFileInfo
          }
          onSetAsMapPhotograph={onSetAsMapPhotograph}
          onEditPhotograph={onStartEditPhotograph}
          isMapPhotograph={get(gallery, "map_photograph") === photo.id}
          showSoldIcons={showSoldIcons}
          key={index}
          index={index}
          photo={photo}
          left={left}
          top={top}
          country_id={get(gallery, ["gallery_price", "country"])}
        />
      );
    },
    [
      activePhotographId,
      gallery_id,
      get(gallery, "map_photograph"),
      selectedPhotographIds,
      showSoldIcons,
    ],
  );

  const renderEditPhotograph = () => {
    return (
      <PhotographEditor
        photograph_id={editingPhotographId}
        country_id={gallery.gallery_price.country}
        onClose={onStopEditPhotograph}
      />
    );
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleLoadMore = () => {
    setPhotosToShow(photosToShow + PHOTOS_PER_PAGE);
  };

  const handleViewSelectedPhotos = () => {
    if (size(selectedPhotographIds) > 0) {
      setShowViewPopup(true);
    }
  };

  const handleCloseViewPopup = () => {
    setShowViewPopup(false);
  };

  const handleDeleteSelectedPhotos = () => {
    if (size(selectedPhotographIds) === 0) {
      return;
    }
    
    const onConfirmed = () => {
      deleteSelectedPhotographs({
        selectedPhotographIds,
        showError,
        dispatch,
        setSelectedPhotographIds,
        showSuccess,
        gallery_id,
        galleryPhotographs,
      })
    }

    dispatch(
      confirmModal({
        text: `Are you sure you want to make ${size(
          selectedPhotographIds,
        )} photograph${size(selectedPhotographIds) > 1 ? "s" : ""} unavailable for purchase?`,
        onConfirmed: onConfirmed,
        can_cancel: true,
      }),
    );
  };

  const filteredPhotographs = searchTerm
    ? filter(galleryPhotographs, (photo) =>
        get(photo, "photo_name", "").toLowerCase().includes(searchTerm.toLowerCase())
      )
    : galleryPhotographs;

  const paginatedPhotographs = slice(filteredPhotographs, 0, photosToShow);
  const hasMorePhotos = size(filteredPhotographs) > photosToShow;

  return (
    <PageLayout
      title={get(gallery, "gallery_name")}
      gallery={gallery}
    >
      {editingPhotographId && renderEditPhotograph()}
      <Dialog open={showViewPopup} onClose={handleCloseViewPopup} maxWidth="sm" fullWidth>
        <DialogTitle className={classes.dialogTitle}>
          <span>Selected Photo Details</span>
          <IconButton onClick={handleCloseViewPopup} size="small" className={classes.closeButton}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {map(selectedPhotographIds, (photoId) => {
            const photo = filter(galleryPhotographs, (p) => p.id === photoId)[0];
            if (!photo) return null;
            return (
              <div key={photoId} className={classes.photoDetail}>
                <div>
                  <span className={classes.photoDetailLabel}>ID:</span>
                  <span>{get(photo, "id")}</span>
                </div>
                <div>
                  <span className={classes.photoDetailLabel}>Photo Name:</span>
                  <span>{get(photo, "photo_name")}</span>
                </div>
                <div>
                  <span className={classes.photoDetailLabel}>Available to Purchase:</span>
                  <span>{get(photo, "available_to_purchase") ? "Yes" : "No"}</span>
                </div>
                <div>
                  <span className={classes.photoDetailLabel}>Price:</span>
                  <span>{get(photo, "price")}</span>
                </div>
                <div>
                  <span className={classes.photoDetailLabel}>Times Sold:</span>
                  <span>{get(photo, "purchased_trolley_photographs", []).length}</span>
                </div>
              </div>
            );
          })}
        </DialogContent>
      </Dialog>

      {loadingGalleries && <LoadingSpinner />}
      <div className={classes.photographs_container}>
        <input type="text" autoComplete="username" hidden />
        <TextField
          className={classes.searchBox}
          name="texthere"
          placeholder="Search by photo name..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <div
          className={`${classes.soldIconButton} ${showSoldIcons ? classes.soldIconButtonActive : classes.soldIconButtonInactive}`}
          onClick={handleToggleSoldIcons}
        >
          <LocalOfferIcon fontSize="small" />
        </div>
        <div className={classes.deleteIconButton} onClick={handleDeleteSelectedPhotos}>
          <DeleteIcon />
        </div>
        <div className={classes.eyeIconButton} onClick={handleViewSelectedPhotos}>
          <VisibilityIcon />
        </div>
        {!loadingGalleries && paginatedPhotographs && (
          <>
            <Gallery photos={paginatedPhotographs} renderImage={imageRenderer} />
          </>
        )}
        {!loadingGalleries &&
          get(gallery, "id") &&
          size(galleryPhotographs) === 0 && (
            <div>There are no photographs in this gallery</div>
          )}
      </div>
      {!loadingGalleries && hasMorePhotos && (
        <div className={classes.paginationContainer}>
          <Button className={classes.loadMoreButton} onClick={handleLoadMore}>
            Load More ({size(galleryPhotographs) - photosToShow} remaining)
          </Button>
        </div>
      )}
    </PageLayout>
  );
};
